import { faUsers, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { createContext, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import EmployeeGroupQuickfilter from "src/containers/Quickfilters/EmployeeGroupQuickfilter/EmployeeGroupQuickfilter";
import MyTeamMembers from "src/views/my-team-configuration/MyTeamMembers/MyTeamMembers";
import { MyTeamConfigurationLinks } from "src/widgetLink";
import MyTeamPlanning from "./MyTeamPlanning/MyTeamPlanning";
import LoadingOverlay from "../../containers/LoadingOverlay/LoadingOverlay";
import { useEmployeeGroupQuickFilter } from "../../containers/Quickfilters/EmployeeGroupQuickfilter";
import { EmployeeGroupOptionValue } from "../../containers/Quickfilters/EmployeeGroupQuickfilter/interfaces";
import {
  LegalEntityQuickfilter,
  useLegalEntityQuickFilter,
} from "../../containers/Quickfilters/LegalEntityQuickfilter";
import { LegalEntityOptionValue } from "../../containers/Quickfilters/LegalEntityQuickfilter/interfaces";
import { useAppSelector } from "../../custom-hooks/redux/redux";
import { isTabAuthorized } from "../../custom-hooks/useAuthorization/useAuthorization";
import useQuery from "../../custom-hooks/useQuery/useQuery";
import { CompanyLegalEntityDTO } from "../../models/company-accounts/company-legal-entities";
import { Panel } from "../../reusable-components/MultipleTabs/interfaces";
import MultipleTabs from "../../reusable-components/MultipleTabs/MultipleTabs";
import { NotAuthorizedPage } from "../../reusable-components/NotAuthorizedPage/NotAuthorizedPage";

interface Filter {
  legalEntity: {
    legalEntityOption: LegalEntityOptionValue | null;
    selectedLegalEntity: CompanyLegalEntityDTO | null;
    legalEntityQuickFilter: JSX.Element | null;
  };
  employeeGroup: {
    employeeGroupOption: EmployeeGroupOptionValue | null;
    employeeGroupQuickFilter: JSX.Element | null;
  };
}

export const MyTeamConfigurationContext = createContext<Filter>({
  legalEntity: {
    legalEntityOption: null,
    selectedLegalEntity: null,
    legalEntityQuickFilter: null,
  },
  employeeGroup: {
    employeeGroupOption: null,
    employeeGroupQuickFilter: null,
  },
});

export enum MY_TEAM_CONFIGURATION {
  TEAM_MEMBERS = "team-members",
  TEAM_PLANNING = "team-planning",
}

const MyTeamConfigurationPage = () => {
  const { t } = useTranslation();
  const tRP = "components.widgets.my_team_configuration"; // tRP - translation root path

  const {
    legalEntitiesQuickFilter,
    setLegalEntitiesQuickFilter,
    selectedLegalEntity,
    legalEntityQFIsLoading,
    allLegalEntities,
  } = useLegalEntityQuickFilter();

  const { employeeGroupQuickFilter, setEmployeeGroupQuickFilter } = useEmployeeGroupQuickFilter();

  const { currentUserAuthorizations, currentUserData } = useAppSelector((state) => state.currentUserReducer);

  const checkPanelAccess = useCallback((showPanel: boolean, panel: Panel) => (showPanel ? [panel] : []), []);

  const allowMyTeamConfiguration = isTabAuthorized(
    MyTeamConfigurationLinks.TeamMembers,
    currentUserAuthorizations,
    currentUserData
  );

  const query = useQuery();

  const panels: Panel[] = useMemo(
    () => [
      ...checkPanelAccess(allowMyTeamConfiguration, {
        tabLabel: t(`${tRP}.team_members`),
        tabIcon: <FontAwesomeIcon icon={faUsers} />,
        tableTitle: t(`${tRP}.team_members`),
        tabPanelComponent: <MyTeamMembers />,
        routePath: MY_TEAM_CONFIGURATION.TEAM_MEMBERS,
      }),
      ...checkPanelAccess(allowMyTeamConfiguration, {
        tabLabel: t(`${tRP}.team_planning`),
        tabIcon: <FontAwesomeIcon icon={faCalendarAlt} />,
        tableTitle: t(`${tRP}.team_planning`),
        tabPanelComponent: <MyTeamPlanning />,
        routePath: MY_TEAM_CONFIGURATION.TEAM_PLANNING,
      }),
    ],
    [t]
  );

  const selectedTab = useMemo(() => {
    const foundIndex = panels.findIndex((panel) => panel.routePath === query.get("tab"));
    return foundIndex >= 0 ? foundIndex : 0;
  }, [query, panels]);

  return (
    <>
      {panels.length ? (
        <MyTeamConfigurationContext.Provider
          value={{
            legalEntity: {
              legalEntityOption: legalEntitiesQuickFilter.legalEntities,
              selectedLegalEntity,
              legalEntityQuickFilter: (
                <LegalEntityQuickfilter
                  quickFilters={legalEntitiesQuickFilter}
                  handleChangeQuickFilters={setLegalEntitiesQuickFilter}
                  allLegalEntitiesOption
                  allLegalEntities={allLegalEntities}
                />
              ),
            },
            employeeGroup: {
              employeeGroupOption: employeeGroupQuickFilter.employeeGroups,
              employeeGroupQuickFilter: (
                <EmployeeGroupQuickfilter
                  quickFilters={employeeGroupQuickFilter}
                  handleChangeQuickFilters={setEmployeeGroupQuickFilter}
                  allEmployeeGroupsOption
                />
              ),
            },
          }}
        >
          <LoadingOverlay loading={legalEntityQFIsLoading}>
            {!legalEntityQFIsLoading && (
              <MultipleTabs
                panels={panels}
                selectedTab={selectedTab}
              />
            )}
          </LoadingOverlay>{" "}
        </MyTeamConfigurationContext.Provider>
      ) : (
        <NotAuthorizedPage />
      )}
    </>
  );
};

export default MyTeamConfigurationPage;
